import { AxiosError, AxiosResponse } from "axios";
import { Button, Container, Form, Link, Message, Stack, Text } from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { OneTimeCode } from "components/OneTimeCode/OneTimeCode";
import { SvgInline } from "components/SvgInline/SvgInline";
import { putHref } from "library/api";
import { Dispatch, useState } from "react";
import { useMutation } from "react-query";
import { Actions, ActionTypes, OTPError } from "./types";

interface Props {
  email: string;
  dispatch: Dispatch<Actions>;
  verifyEmailUrl: string;
}

type VerifyEmailCodePayload = {
  verification_code: string;
};

export const VerifyEmail = ({ email, dispatch, verifyEmailUrl }: Props) => {
  const [showRedirectMessage, setShowRedirectMessage] = useState<
    "2FA_REQUIRED" | "OTP_Exceeded_Allowed_Limits" | ""
  >("");
  const verifyEmailMutation = useMutation<
    AxiosResponse<void>,
    AxiosError | AxiosError<OTPError>,
    VerifyEmailCodePayload
  >(verifyEmailUrl, async ({ verification_code }) =>
    putHref<void, VerifyEmailCodePayload>(verifyEmailUrl, {
      verification_code,
    }),
  );

  const onSubmit = async ({ errors, values, setErrors }) => {
    if (errors.length > 0) return;

    verifyEmailMutation.mutate(
      { verification_code: values.oneTimeCode },
      {
        onSuccess() {
          dispatch({ type: ActionTypes.SUCCESS, email });
        },
        onError(e) {
          if (e.response?.data.type === "2FA_REQUIRED") {
            setShowRedirectMessage("2FA_REQUIRED");
          } else if (e.response?.data.title === "OTP_Mismatch") {
            setErrors({ oneTimeCode: "invalid code" });
          } else if (e.response?.data.title === "OTP_Exceeded_Allowed_Limits") {
            setShowRedirectMessage("OTP_Exceeded_Allowed_Limits");
          } else {
            // TODO: handle OTP failure here
            dispatch({ type: ActionTypes.ERROR });
          }
        },
      },
    );
  };

  return (
    <Form onSubmit={onSubmit} initialValues={{ oneTimeCode: "" }}>
      <Stack gap="8">
        <SvgInline name="icon-security-lock" size="92" />
        <Text as="h3" textStyle="heading5" textStyle-sm="heading4">
          Verify your identity
        </Text>
        {showRedirectMessage === "2FA_REQUIRED" && (
          <Message
            severity="info-or-minor"
            bg="secondary.pink.t30"
            title="Oops, something went wrong"
          >
            We were unable to complete the update of your email address. Return
            to the{" "}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                dispatch({ type: ActionTypes.BACK });
              }}
              newTab={false}
            >
              edit page
            </Link>{" "}
            and we'll send you a new verification code.
          </Message>
        )}
        {showRedirectMessage === "OTP_Exceeded_Allowed_Limits" && (
          <Message
            severity="info-or-minor"
            bg="secondary.pink.t30"
            title="Verification code expired"
          >
            The verification code we sent you via email has now expired. Please{" "}
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                dispatch({ type: ActionTypes.BACK });
              }}
              newTab={false}
            >
              enter your details
            </Link>{" "}
            again.
          </Message>
        )}
        <OneTimeCode
          showResend={false}
          otpRecipient={email}
          resendMutationFn={() => Promise.resolve()} // TODO: replace with the proper retry functionality
          onResendCodeError={(err) => {
            if (err) {
              dispatch({ type: ActionTypes.ERROR });
            }
          }}
        />
        <Message
          severity="assistance"
          bg="secondary.lightBlue.t25"
          title="Have a different email address?"
        >
          If you have not received the email verification code, please check
          your email address above is correct.
        </Message>
        <Container width="100%" width-sm="400">
          <FormActions>
            <FormActions.Primary>
              <Button width="100%" type="submit">
                Verify
              </Button>
            </FormActions.Primary>
            <FormActions.Secondary>
              <Button
                variant="secondary"
                onClick={() => {
                  dispatch({ type: ActionTypes.BACK });
                }}
                width="100%"
              >
                Back
              </Button>
            </FormActions.Secondary>
          </FormActions>
        </Container>
      </Stack>
    </Form>
  );
};
